import { useState, FormEvent } from 'react';
import Modal from '../../components/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../services/api';
import './RegisterMenu.css';

interface RegisterMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

/**
 * Register modal menu component
 * Handles new user account creation
 * @param isOpen - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param onSwitchToLogin - Callback to switch to login modal
 * @returns The rendered register modal component
 */
function RegisterMenu({ isOpen, onClose, onSwitchToLogin }: RegisterMenuProps): JSX.Element {
  const { register } = useAuth();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  /**
   * Handles form submission for registration
   * @param event - Form submit event
   */
  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await register(username, password);
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Resets form state when modal closes
   */
  const handleClose = (): void => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Register">
      <form className="register-form" onSubmit={handleSubmit}>
        {error && <div className="register-error">{error}</div>}

        <div className="register-field">
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            autoComplete="username"
          />
        </div>

        <div className="register-field">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
        </div>

        <div className="register-field">
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isSubmitting}
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className="register-submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating account...' : 'Register'}
        </button>

        <div className="register-switch">
          <span>Already have an account? </span>
          <button type="button" className="register-switch-btn" onClick={onSwitchToLogin}>
            Login
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default RegisterMenu;
